import {
  AnyAction,
  isFulfilled,
  isPending,
  isRejected,
  Middleware,
  ThunkDispatch,
} from "@reduxjs/toolkit";
import type { RootState } from "../Store";
import { applicationSessionChanged } from "./GlobalActions";

interface AbortableRequest extends Promise<unknown> {
  requestId: string;
  abort(reason?: string): void;
}

function isAbortableRequest(value: unknown): value is AbortableRequest {
  return (
    value !== null &&
    typeof value === "object" &&
    "abort" in value &&
    typeof value.abort === "function" &&
    "requestId" in value &&
    typeof value.requestId === "string" &&
    "then" in value &&
    typeof value.then === "function"
  );
}

export function createApplicationSessionMiddleware(): Middleware<object, RootState> {
  const requests = new Map<string, AbortableRequest>();
  const requestEpochs = new Map<string, number>();
  return (api) => (next) => (action) => {
    const epoch = api.getState().session.epoch;
    if (typeof action === "function") {
      // The thunk and every callback retaining its dispatch belong to this epoch.
      const scopedDispatch: typeof api.dispatch = (nextAction) => {
        if (api.getState().session.epoch !== epoch) {
          if (typeof nextAction === "function") {
            throw new DOMException("Authentication session changed", "AbortError");
          }
          return nextAction;
        }
        return api.dispatch(nextAction);
      };
      const dispatchNext = next as ThunkDispatch<RootState, unknown, AnyAction>;
      const result: unknown = dispatchNext((_: unknown, getState: () => RootState, extra: unknown) =>
        action(scopedDispatch, getState, extra)
      );
      if (isAbortableRequest(result)) {
        requests.set(result.requestId, result);
        const release = () => {
          requests.delete(result.requestId);
        };
        void result.then(release, release);
      }
      return result;
    }
    if (
      applicationSessionChanged.match(action) &&
      action.payload.identityKey !== api.getState().session.identityKey
    ) {
      const previous = [...requests.values()];
      requests.clear();
      requestEpochs.clear();
      const result = next(action);
      previous.forEach((request) => request.abort("Authentication session changed"));
      return result;
    }
    if (isPending(action)) {
      requestEpochs.set(action.meta.requestId, epoch);
    } else if (isFulfilled(action) || isRejected(action)) {
      const owner = requestEpochs.get(action.meta.requestId);
      requestEpochs.delete(action.meta.requestId);
      if (owner !== epoch) {
        return action;
      }
    }
    return next(action);
  };
}
